/**
 * useArticle — React Query hook for `ContentReaderPort.getArticleBySlug`.
 *
 * Wraps the active `ContentReaderPort` (resolved via {@link useContent})
 * in a `useQuery` call. The single-article read path drives the
 * generic `ArticleDetailPage` (TA19) and every bespoke route (TA20.x);
 * those route components consume this hook rather than calling the
 * port directly so loading / error / not-found semantics stay uniform.
 *
 * `null` return semantics
 * =======================
 *
 * The port contract reserves `null` for "no row matched" and the
 * production adapter (`TrpcContentReader`, TA15) forwards `null`
 * verbatim — the API router does NOT map `null` to tRPC `NOT_FOUND`,
 * so a missing article is a sentinel return rather than a thrown
 * error.
 *
 * React Query treats the returned `null` as a successful resolution;
 * `data` becomes `Article | null | undefined` over the lifecycle
 * (`undefined` while pending, then `Article | null` once resolved).
 * Consumers MUST handle the `null` branch as the "not found" UX state
 * — typically a `data === null` check rendering a localised
 * "Artikkelia ei löytynyt" message — rather than treating `null` as
 * "still loading". The hook deliberately does NOT throw on `null`
 * because that would conflate two distinct conditions (no row vs.
 * adapter error) into one `isError` branch.
 *
 * queryKey design
 * ===============
 *
 * Shape: `['content', 'article', slug, locale]`.
 *
 *   - `'content'` is the domain namespace shared by all four TA16
 *     hooks; isolates cache invalidation / prefetching to this module.
 *   - `'article'` is the per-method discriminator.
 *   - `slug` is the primary identifier; two slugs never share a
 *     cache slot.
 *   - `locale` is included because the same slug in different locales
 *     resolves to different rows; omitting it would alias them.
 *
 * The key is intentionally flat (no nested options object) so the
 * cache hits as soon as the same `(slug, locale)` pair is requested
 * again, regardless of object identity at the call site.
 *
 * Authoritative spec references
 * =============================
 *
 *   - `specs/003-content-and-knowledge/tasks.md` TA16.
 *   - `docs/governance/decisions/D-046.md` D-046.
 *   - `apps/web/src/content/ContentReaderPort.ts` (the port being wrapped).
 */

import { type UseQueryResult, useQuery } from '@tanstack/react-query';

import type { Article } from '../ContentReaderPort.js';
import { CONTENT_STALE_TIME } from './queryConfig.js';
import { useContent } from './useContent.js';

/**
 * Options for {@link useArticle}.
 *
 * `locale` is forwarded verbatim to the port. When omitted, the adapter
 * falls back to its own default (per D-051 the production adapter
 * defaults to `'fi'`); the hook does not impose a default of its own
 * so that the cache key reflects exactly what the caller passed.
 */
export interface UseArticleOptions {
  readonly locale?: string;
  /**
   * Gate the underlying `useQuery`. Defaults to `true` (every existing caller
   * fetches as before). When `false`, the query is disabled and no network
   * call is dispatched — used by the US3 preview path, where
   * `ArticleDetailPage` receives a draft `Article` via prop injection and must
   * NOT fetch (the hook is still called unconditionally to honour the Rules of
   * Hooks; it simply does no work).
   */
  readonly enabled?: boolean;
}

/**
 * Fetch a single article by slug. Resolves to `Article` when a row
 * matches, or `null` when no row matches (the "not found" sentinel).
 *
 * Returns the standard React Query result shape. Consumers read
 * `data` as `Article | null | undefined` and discriminate the three
 * states explicitly:
 *
 *   - `isPending` — request in flight; render a loading skeleton.
 *   - `isError` — adapter or network failure; surface via the
 *     `PortErrorBoundary` (T022) or render an inline error.
 *   - `data === null` — article does not exist; render the "not found"
 *     UX state.
 *   - `data` is an `Article` — render normally.
 */
export function useArticle(
  slug: string,
  options: UseArticleOptions = {},
): UseQueryResult<Article | null, Error> {
  const { locale } = options;
  const port = useContent();

  return useQuery<Article | null, Error>({
    queryKey: ['content', 'article', slug, locale],
    queryFn: () => port.getArticleBySlug(slug, { locale }),
    staleTime: CONTENT_STALE_TIME,
    // Default-true: every existing caller fetches unchanged. The preview path
    // passes `enabled: false` so the query is disabled and `queryFn` never
    // runs (no network call) — the hook stays called unconditionally.
    enabled: options.enabled ?? true,
  });
}
